// --------------------------
// IMPORTS
// --------------------------
import { createContext, useContext, useEffect, useRef, useState } from "react";
import "./App.css";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import { message } from "antd";

import secureLocalStorage from "react-secure-storage";

//Components
import Survey from "./crm/views/voc/survey/survey";
import AuditForms from "./crm/views/qms/auditForms/auditForms";

import LogoutHandler from "./crm/views/logoutHandler/logoutHandler";
import { componentMap, roles } from "./routes";
import { GlobalStateContext } from "./crm/utils/GlobalStateContext";
import NotFound from "./crm/views/layouts/NotFound";
import UnderMaintenance from "./crm/views/layouts/UnderMaintenance";
import Main from "./crm/views/layouts/main/main";
import { validateAndLoginThunk } from "./crm/store/thunks/authThunk";
import { useDispatch } from "react-redux";
import Dashboard from "./crm/views/common/dashboard/dashboard";
import { useSelector } from "react-redux";
import CustomLoader from "./crm/views/common/customLoader";
import Login from "./crm/views/common/login/login";
import { persistor } from "./crm/store";
import { clearRedux } from "./crm/store/slices/authSlice";

const UserContext = createContext();

// --------------------------
// MAIN COMPONENT
// --------------------------
const App = () => {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname?.split("/");
  const { setGlobalState } = useContext(GlobalStateContext);
  // --------------------------
  // REDUX STATE
  // --------------------------
  const { isLoggedIn, loading, role } = useSelector((state) => state.auth);

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [authChecked, setAuthChecked] = useState(false);

  const logoutTriggered = useRef(false);

  //const userRole = secureLocalStorage.getItem("role");

  // --------------------------
  // ROUTES
  // --------------------------
  const publicRoutes = ["/survey", "/logout", "/underMaintenance"];
  const isPublicRoute = publicRoutes.some((route) =>
    location.pathname.startsWith(route),
  );

  // --------------------------
  // LOGOUT FUNCTION
  // --------------------------
  const logout = async () => {
    if (logoutTriggered.current) return;

    logoutTriggered.current = true;
    try {
      dispatch(clearRedux());
      await persistor.flush();
      await persistor.purge();

      secureLocalStorage.clear();

      setAuthChecked(true);

      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout Error:", error);

      setAuthChecked(true);
    }
  };

  // --------------------------
  // ACCESS CONTROL
  // --------------------------
  const hasAccess = (role) => {
    if (window.performance) {
      const entries = performance.getEntriesByType("navigation");

      if (entries.length > 0 && entries[0].type === "reload") {
        const searchKeywordOnRefresh = document.querySelector(".inputS");
        if (
          searchKeywordOnRefresh?.value === "" ||
          searchKeywordOnRefresh?.value === undefined
        ) {
          if (secureLocalStorage.getItem("hasSearchedValue")) {
            secureLocalStorage.removeItem("hasSearchedValue");
          }
          if (secureLocalStorage.getItem("searchKeyword")) {
            secureLocalStorage.removeItem("searchKeyword");
          }
        }

        if (
          (location.pathname.split("/")[1] !== "users" ||
            location.pathname.split("/")[1] !== "roles") &&
          location.pathname.split("/")[2]
        ) {
          // Get elements by class name
          let sideNavElements = document.getElementsByClassName("sidenav");
          let rightSideElements = document.getElementsByClassName("right-side");

          // Check if there are elements with the specified class
          if (sideNavElements.length > 0) {
            // Access the first element in the collection and toggle the class
            sideNavElements[0].classList.remove("open");
          }

          if (rightSideElements.length > 0) {
            // Access the first element in the collection and toggle the class
            rightSideElements[0].classList.remove("right-side-open");
          }
        }
      }
    }

    return roles[role] || [];
  };

  const accessibleRoutes = role ? hasAccess(role) : ["/underMaintenance"];

  // --------------------------
  // USE EFFECTS
  // --------------------------

  // --------------------------
  // INITIAL AUTH CHECK
  // --------------------------
  // useEffect(() => {
  //   const accessToken = secureLocalStorage.getItem("accessToken");

  //   if (accessToken) {
  //     setGlobalState((prev) => ({
  //       ...prev,
  //       isLoggedIn: true,
  //     }));

  //     if (location.pathname === "/") {
  //       navigate("/dashboard", {
  //         replace: true,
  //       });
  //     }
  //   }
  // }, [location.pathname, navigate, setGlobalState]);

  useEffect(() => {
    let timer;

    const validateAuth = async () => {
      try {
        // skip auth validation for public routes
        if (isPublicRoute || location.pathname === "/") {
          setAuthChecked(true);
          return;
        }

        const accessToken = secureLocalStorage.getItem("accessToken");

        const expiry = secureLocalStorage.getItem("accessTokenExpiry");

        // missing token or expiry
        if (!accessToken || !expiry) {
          setAuthChecked(true);

          // only logout if not already on login page
          if (location.pathname !== "/") {
            await logout();
          }

          return;
        }

        const now = Date.now();

        // token expired
        if (now >= Number(expiry)) {
          setAuthChecked(true);

          if (location.pathname !== "/") {
            await logout();
          }

          return;
        }

        // valid session
        setGlobalState((prev) => ({
          ...prev,
          isLoggedIn: true,
        }));

        // auto logout timer
        const timeout = Number(expiry) - now;

        if (isNaN(timeout) || timeout <= 0) {
          await logout();
          return;
        }
        timer = setTimeout(async () => {
          await logout();
        }, timeout);

        // redirect to dashboard if on login page
        if (location.pathname === "/") {
          navigate("/dashboard", {
            replace: true,
          });
        }

        setAuthChecked(true);

        timer = setTimeout(async () => {
          await logout();
        }, timeout);
      } catch (error) {
        console.error("Auth Validation Error:", error);

        setAuthChecked(true);
      }
    };

    validateAuth();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);
  //To show notification using Ant Design's notification component
  const showNotification = (errorMsg, moreThan = "no") => {
    message.error({
      content: (
        <div style={{ whiteSpace: "pre-line" }}>
          {moreThan === "yes" ? (
            <strong>Errors occurred:</strong>
          ) : (
            <strong>An error occurred:</strong>
          )}
          <p>{errorMsg}</p> {/* This ensures line breaks are respected */}
        </div>
      ),
      duration: 5, // duration in seconds
      className: "error-message", // Add any custom styles you need
    });
  };

  const clearAndLogout = async () => {
    secureLocalStorage.clear();
    localStorage.setItem("logout", Date.now()); // Triggers storage event
    //window.location.href = process.env.REACT_APP_SSO_LOGIN_REDIRECT_URI;
  };

  if (!authChecked) {
    return <CustomLoader />;
  }

  return (
    <UserContext.Provider
      value={{
        loggedIn: isLoggedIn,
      }}
    >
      <Routes>
        {/* ROOT */}
        <Route
          path="/"
          element={
            isLoggedIn ? <Navigate to="/dashboard" replace /> : <Login />
          }
        />
        {/* DASHBOARD */}
        <Route
          path="/dashboard"
          element={
            isLoggedIn ? (
              <Main
                children={<Dashboard />}
                key_elem={"1"}
                nameLink="Dashboard"
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {/* PUBLIC */}
        <Route path="/survey/:id" element={<Survey />} />
        <Route path="/logout/:empID" element={<LogoutHandler />} />
        <Route path="/underMaintenance" element={<UnderMaintenance />} />
        {/* ROLE BASED ROUTES */}
        {accessibleRoutes.map((route) => (
          <Route
            key={route}
            path={route}
            element={
              isLoggedIn ? componentMap[route] : <Navigate to="/" replace />
            }
          />
        ))}
        {/* 404 */}
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/" replace />} />{" "}
      </Routes>
    </UserContext.Provider>
  );
};

export default App;
export { UserContext };
